import { t } from "i18next";
import { displayToast } from "../../../components/app/AppToast";
import {
  isDesktopShell,
  isNativeApp,
} from "../../../common/hooks/useIsDesktopView";
import { FLAG_SHARE_URL_ITEM } from "../../../config/featureFlags";
import { SoundEffect } from "../../../interfaces";
import { androidShare, isAndroidApp } from "../../../tools/android";
import { iosPlaySound } from "../../../tools/ios";

/**
 * What became of a share: the link went to the clipboard, the reader handed
 * it to a share target, the reader closed the sheet, or the click came while
 * another share was running and opened nothing.
 */
export type ShareOutcome = "copied" | "sent" | "cancelled" | "busy";

/**
 * Writes `text` to the clipboard. Call it inside the click, before any await:
 * Safari refuses a write once the user gesture is lost, so the item takes the
 * promise and fills itself when the text resolves.
 */
export async function copyToClipboard(text: Promise<string>): Promise<void> {
  await navigator.clipboard.write([
    new ClipboardItem({
      "text/plain": text.then(
        (value) => new Blob([value], { type: "text/plain" })
      ),
    }),
  ]);
}

let sharing = false;

/** A share sheet or copy is running: a click now would open nothing. */
export function isSharing(): boolean {
  return sharing;
}

/**
 * Shares a link. A desktop browser copies it and says so in a toast, as the
 * collection page does: the share sheet of a desktop OS is a dialog the reader
 * did not ask for. Elsewhere the share sheet opens with `text` and the link.
 * The desktop is the device, as for the shell (`isDesktopShell`), not the
 * viewport: a phone in landscape keeps its sheet, a narrowed window its copy.
 * Call it inside the click, before any await, with the link as a promise: the
 * copy needs the user gesture.
 */
// `text` null shares the link alone: a copy then gives the link, nothing more.
export async function shareUrl(
  title: string,
  text: string | null,
  link: Promise<string>
): Promise<ShareOutcome> {
  // A second click while a share runs: its share is recorded all the same,
  // so a failure still reaches the caller's error toast.
  if (sharing) {
    await link;
    return "busy";
  }
  sharing = true;
  try {
    if (isDesktopShell() && !isNativeApp()) {
      await copyToClipboard(link);
      displayToast(t("Link copied"), "success");
      return "copied";
    }

    const url = await link;

    if (isAndroidApp()) {
      iosPlaySound(SoundEffect.SHARE);
      androidShare(url, text);
      return "sent";
    }

    // Check if share is supported (may be unavailable in some browsers)
    const canShare = typeof navigator.share === "function";
    if (!canShare) {
      try {
        await navigator.clipboard.writeText(
          text === null ? url : text + " \n" + url
        );
        displayToast(text === null ? t("Link copied") : t("Copied"), "success");
      } catch (error) {
        if (globalThis.location.protocol === "http:")
          displayToast(t("Sharing needs a secure connection."), "error");
        else displayToast(t("Your browser doesn't support sharing."), "error");

        throw error;
      }
      return "copied";
    }

    // The url is always embedded in `text` (like the Android bridge does)
    // because some targets (WhatsApp, etc.) read ONLY the `text` field and
    // drop a separate `url`, so the link would vanish.
    // The `url` member fixes AirDrop: without a real URL item, AirDrop
    // serializes the share as a .txt file that lands in the recipient's
    // Files app instead of opening the app. It is staging-gated
    // (FLAG_SHARE_URL_ITEM) until QA clears the tradeoff that a few
    // targets (Mail, Notes) render the link twice.
    // The link alone is a real URL item, which every target reads, AirDrop
    // included; with no text beside it, nothing is rendered twice.
    await navigator.share(
      text === null
        ? { title, url }
        : FLAG_SHARE_URL_ITEM
          ? { title, text: text + " \n" + url, url }
          : { title, text: text + " \n" + url }
    );

    iosPlaySound(SoundEffect.SHARE);
    return "sent";
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError")
      return "cancelled";
    console.error("Couldn't share URL.", error);
    throw error;
  } finally {
    sharing = false;
  }
}
